/**
 * DocumentTwinLinksPanel — "Verknüpfte Objekte" for one archived document
 * (FR-017.7).
 *
 * The Belege panel answers "which documents back this entity?". This is the
 * other direction, which did not exist at all before FR-017: standing in
 * front of a scanned certificate, what did it actually produce in the twin?
 *
 * Links are grouped by type, because the farmer's question is about the
 * KIND of relationship ("what does this document prove?") rather than about
 * the individual rows. `generic` is shown here, unlike on the Belege panel:
 * there a "generic" badge would mark every tile and say nothing, whereas
 * here it is a real answer — this document was routed onto that object and
 * asserts nothing further about it.
 *
 * Each row reads like the object it points to — an icon for its kind, its
 * name (or, for a nameless invoice or cash flow, its number), and one quiet
 * line of what it is: kind, counterparty, date, amount. Never an id: the
 * panel used to print `cash_flow · cf-inv-26048c92-…`, which means nothing
 * to a farmer. The actions are small grey icons that light up on hover.
 */

import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import {
  ArrowUpRight,
  Box,
  FileSignature,
  Link2,
  Plus,
  Receipt,
  Sprout,
  Tractor,
  Trash2,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';

import { linkDetails } from '../../components/twinLinks/linkFormat';
import type { LineRow } from '../../components/twinLinks/lineRows';
import { documentLinksKey, refreshLinkQueries } from '../../components/twinLinks/linkQueries';
import { linkTargetDisplay, type TargetIconKind } from '../../components/twinLinks/linkTargetDisplay';
import { linkTypeLabel } from '../../services/linkTypeLabels';
import { twinService, type DocumentLink } from '../../services/twinService';
import AddDocumentLinkForm, { type EntitySuggestion, type LinkTargetType } from './AddDocumentLinkForm';
import './DocumentTwinLinksPanel.css';

interface DocumentTwinLinksPanelProps {
  documentId: string;
  /**
   * Where a twin object lives in the farm explorer, or `null` when it has no
   * page there. Returning `null` is the normal answer for an event type such
   * as `delivery_event`, which has no lens: the target then renders as plain
   * text rather than as a link to a page that does not exist.
   */
  targetHref?: (entityType: string, entityId: string) => string | null;
  /** Object types the farmer may link to. Absent or empty hides "add". */
  addTargets?: readonly LinkTargetType[];
  searchEntities?: (entityType: string, q: string) => Promise<EntitySuggestion[]>;
  /** The document's extracted rows and date — the add form's line picker (FR-017 #5). */
  lines?: readonly LineRow[];
  documentDate?: string | null;
}

/** Stable display order; anything unknown sorts after, alphabetically. */
const TYPE_ORDER = ['validity', 'ownership', 'price', 'quantity', 'generic'];

function byTypeOrder(a: string, b: string): number {
  const ia = TYPE_ORDER.indexOf(a);
  const ib = TYPE_ORDER.indexOf(b);
  if (ia === -1 && ib === -1) return a.localeCompare(b);
  if (ia === -1) return 1;
  if (ib === -1) return -1;
  return ia - ib;
}

const KIND_ICONS: Readonly<Record<TargetIconKind, LucideIcon>> = {
  receipt: Receipt,
  money: Wallet,
  contract: FileSignature,
  land: Sprout,
  people: Users,
  machine: Tractor,
  object: Box,
};

const ICON_SIZE = 16;

export const DocumentTwinLinksPanel: React.FC<DocumentTwinLinksPanelProps> = ({
  documentId,
  targetHref,
  addTargets,
  searchEntities,
  lines,
  documentDate,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery<DocumentLink[]>({
    queryKey: documentLinksKey(documentId),
    queryFn: () => twinService.getDocumentLinks(documentId),
    enabled: Boolean(documentId),
  });

  const [pendingUnlink, setPendingUnlink] = React.useState<string | null>(null);
  // Keyboard focus follows the inline confirmation: onto its button when it
  // opens, back onto the row's remove icon when it is cancelled — the
  // actions it replaces unmount, which would otherwise drop focus to <body>.
  const confirmButton = React.useRef<HTMLButtonElement | null>(null);
  const removeButtons = React.useRef(new Map<string, HTMLButtonElement>());
  const [refocus, setRefocus] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (pendingUnlink) confirmButton.current?.focus();
  }, [pendingUnlink]);
  React.useEffect(() => {
    if (refocus && !pendingUnlink) {
      removeButtons.current.get(refocus)?.focus();
      setRefocus(null);
    }
  }, [refocus, pendingUnlink]);

  const [adding, setAdding] = React.useState(false);

  const refreshBothDirections = React.useCallback(() => refreshLinkQueries(queryClient), [queryClient]);

  const unlink = useMutation({
    mutationFn: (linkId: string) => twinService.supersedeLink(linkId),
    onSuccess: () => {
      setPendingUnlink(null);
      refreshBothDirections();
    },
  });

  const canAdd = Boolean(searchEntities) && (addTargets?.length ?? 0) > 0;

  /** The lens's own name for a type the locale files do not know yet. */
  const lensLabel = React.useMemo(() => {
    const byType = new Map<string, string>();
    for (const target of addTargets ?? []) byType.set(target.entityType, target.label);
    return byType;
  }, [addTargets]);

  const links = React.useMemo(() => data ?? [], [data]);
  const grouped = React.useMemo(() => {
    const byType = new Map<string, DocumentLink[]>();
    for (const link of links) {
      const bucket = byType.get(link.link_type);
      if (bucket) bucket.push(link);
      else byType.set(link.link_type, [link]);
    }
    return [...byType.entries()].sort((a, b) => byTypeOrder(a[0], b[0]));
  }, [links]);

  const heading = (
    <header className="document-twin-links__header">
      <h3
        className="document-twin-links__heading"
        aria-label={
          links.length > 0 ? t('twinLinks.headingWithCount', { title: t('twinLinks.title'), count: links.length }) : undefined
        }
      >
        {t('twinLinks.title')}
        {links.length > 0 && (
          <span className="document-twin-links__count" aria-hidden="true">
            {links.length}
          </span>
        )}
      </h3>
      {/* An empty panel offers „add" as a labelled button under its hint instead. */}
      {canAdd && !adding && links.length > 0 && (
        <button
          type="button"
          className="document-twin-links__icon-btn"
          onClick={() => setAdding(true)}
          aria-label={t('twinLinks.add')}
          title={t('twinLinks.add')}
        >
          <Plus size={ICON_SIZE} aria-hidden="true" />
        </button>
      )}
    </header>
  );

  if (isLoading) {
    return (
      <section className="document-twin-links" aria-busy="true">
        {heading}
        <p className="document-twin-links__status" data-testid="document-twin-links-loading">
          {t('twinLinks.loading')}
        </p>
      </section>
    );
  }

  if (isError) {
    return (
      <section className="document-twin-links">
        {heading}
        <p className="document-twin-links__status">{t('twinLinks.loadError')}</p>
        <button type="button" className="document-twin-links__text-btn" onClick={() => void refetch()}>
          {t('twinLinks.retry')}
        </button>
      </section>
    );
  }

  return (
    <section className="document-twin-links">
      {heading}

      {links.length === 0 && !adding && (
        <div className="document-twin-links__empty" data-testid="document-twin-links-empty">
          <Link2 size={ICON_SIZE} aria-hidden="true" className="document-twin-links__empty-icon" />
          <div>
            <p>{t('twinLinks.empty')}</p>
            {canAdd && <p className="document-twin-links__hint">{t('twinLinks.emptyHint')}</p>}
          </div>
        </div>
      )}

      {grouped.map(([linkType, rows]) => (
        <div key={linkType} className="document-twin-links__group">
          <h4 className="document-twin-links__group-heading">{linkTypeLabel(linkType)}</h4>
          <ul role="list" className="document-twin-links__list">
            {rows.map((link) => {
              const shown = linkTargetDisplay(link, lensLabel.get(link.target_type));
              const href = targetHref?.(link.target_type, link.target_id) ?? null;
              const details = linkDetails(link);
              const Icon = KIND_ICONS[shown.kind];
              const confirming = pendingUnlink === link.link_id;
              // Rows titled only by their kind („Maßnahme") would share one label.
              const labelName = shown.isFallback ? [shown.title, ...shown.meta].join(', ') : shown.title;
              return (
                <li
                  key={link.link_id}
                  className={`document-twin-links__row${confirming ? ' document-twin-links__row--confirming' : ''}`}
                >
                  <span className="document-twin-links__kind" aria-hidden="true">
                    <Icon size={ICON_SIZE} />
                  </span>

                  <div className="document-twin-links__body">
                    {href ? (
                      <Link className="document-twin-links__target" to={href} title={shown.title}>
                        {shown.title}
                      </Link>
                    ) : (
                      <span className="document-twin-links__target" title={shown.title}>
                        {shown.title}
                      </span>
                    )}
                    {shown.meta.length > 0 && <span className="document-twin-links__meta">{shown.meta.join(' · ')}</span>}
                    {details.length > 0 && (
                      <span className="document-twin-links__chips">
                        {details.map((detail) => (
                          <span key={detail} className="document-twin-links__chip">
                            {detail}
                          </span>
                        ))}
                      </span>
                    )}

                    {confirming && (
                      // Confirmation is inline rather than a window.confirm: the
                      // unlink is durable (it stops the pipeline recreating the
                      // link), so the farmer should read what they are about to
                      // do without a modal stealing focus.
                      <span className="document-twin-links__confirm" role="group" aria-label={t('twinLinks.confirmRemove')}>
                        <span>{t('twinLinks.confirmRemove')}</span>
                        <button
                          ref={confirmButton}
                          type="button"
                          className="document-twin-links__text-btn document-twin-links__text-btn--danger"
                          onClick={() => unlink.mutate(link.link_id)}
                          disabled={unlink.isPending}
                        >
                          {t('twinLinks.confirm')}
                        </button>
                        <button
                          type="button"
                          className="document-twin-links__text-btn"
                          onClick={() => {
                            setPendingUnlink(null);
                            setRefocus(link.link_id);
                          }}
                        >
                          {t('twinLinks.cancel')}
                        </button>
                      </span>
                    )}
                  </div>

                  {!confirming && (
                    <span className="document-twin-links__actions">
                      {href && (
                        <Link
                          className="document-twin-links__icon-btn"
                          to={href}
                          aria-label={t('twinLinks.open', { name: labelName })}
                          title={t('twinLinks.open', { name: shown.title })}
                        >
                          <ArrowUpRight size={ICON_SIZE} aria-hidden="true" />
                        </Link>
                      )}
                      <button
                        ref={(el) => {
                          if (el) removeButtons.current.set(link.link_id, el);
                          else removeButtons.current.delete(link.link_id);
                        }}
                        type="button"
                        className="document-twin-links__icon-btn document-twin-links__icon-btn--danger"
                        onClick={() => setPendingUnlink(link.link_id)}
                        aria-label={t('twinLinks.remove', { name: labelName })}
                        title={t('twinLinks.remove', { name: shown.title })}
                      >
                        <Trash2 size={ICON_SIZE - 1} aria-hidden="true" />
                      </button>
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}

      {canAdd &&
        (adding ? (
          <AddDocumentLinkForm
            documentId={documentId}
            targetTypes={addTargets!}
            searchEntities={searchEntities!}
            lines={lines}
            documentDate={documentDate}
            onCreated={() => {
              setAdding(false);
              refreshBothDirections();
            }}
            onCancel={() => setAdding(false)}
          />
        ) : (
          links.length === 0 && (
            <button
              type="button"
              className="document-twin-links__text-btn document-twin-links__add-toggle"
              onClick={() => setAdding(true)}
            >
              <Plus size={ICON_SIZE - 2} aria-hidden="true" />
              {t('twinLinks.add')}
            </button>
          )
        ))}

      {unlink.isError && (
        <p role="alert" className="document-twin-links__error">
          {t('twinLinks.removeError')}
        </p>
      )}
    </section>
  );
};

export default DocumentTwinLinksPanel;
